import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  CalendarDays,
  Clock,
  Users,
  CheckCircle,
  AlertTriangle,
  Save,
  X,
  Plus,
  Edit,
  Trash2,
  Eye,
  Euro,
  Info,
  Tag,
  Repeat
} from 'lucide-react';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import PrestationTypePicker from './PrestationTypePicker';
import CatalogueRubriqueSelect from './CatalogueRubriqueSelect';
import { getDefaultPrestationType, getPrestationTypeLabel } from '../config/prestation-types';
import apiService from '../services/api';
import {
  getDefaultTauxTVA,
  isRestaurationCategorie,
  normalizeTauxTVAInput,
  TVA_RATE_OPTIONS,
} from '../utils/tvaDefaults';
import { uploadMultipleImagesToCloudinary } from '../utils/uploadCloudinary';
import {
  CLOUDINARY_ADMIN_WIDTH,
  cloudinaryDeliveryUrl,
} from '../utils/cloudinaryDelivery';
import { DUREE_PRESETS, describeDureesSite, roundMoney } from '../utils/creneauConfig';
import {
  CAPACITE_MODE,
  buildCreneauCapacitePayload,
  formatCreneauCapaciteListLabel,
  creneauFormCapaciteFromStored,
  defaultCreneauFormCapacite,
} from '../utils/prestationCreneauCapacite';
import { getSpaceUiConfig } from './prestation-wizard/spaceLabels.js';
import {
  getPrestationModalUi,
  getPrestationTypeSwitchPatch,
  shouldShowRessourceLink,
} from '../config/prestationModalUi.js';
import PrestationDisponibiliteSection from './prestation/PrestationDisponibiliteSection.jsx';
import PrestationEquipementSection from './prestation/PrestationEquipementSection.jsx';
import PrestationPreviewModal from './prestation/PrestationPreviewModal.jsx';
import CommercantHorairesSubModal from './prestation/CommercantHorairesSubModal.jsx';
import { buildPlagesFromCommercantHoraires } from '../utils/prestationDisponibilite.js';
import {
  hasEquipementFourni,
  parsePreparationRequis,
  serializePreparationRequis,
} from '../utils/prestationEquipementDetails.js';
import { formatInstructeurName } from '../utils/instructeurLabels.js';
import {
  NECESSITE_PERSONNEL_HELP,
  NECESSITE_PERSONNEL_OPTIONS,
  defaultNecessitePersonnelForCreate,
  necessitePersonnelFromSelectValue,
  necessitePersonnelToSelectValue,
} from '../utils/necessitePersonnelForm.js';
import {
  DEPOSIT_AMOUNT_HELP,
  DEPOSIT_AMOUNT_LABEL,
  DEPOSIT_OVER_BASE_PRICE_WARNING,
  PAYMENT_COLLECTION_OPTIONS,
  PAYMENT_COLLECTION_SECTION_HELP,
  PAYMENT_COLLECTION_SECTION_TITLE,
  PAYMENT_WHEN_RESERVATION_OFFLINE_HELP,
  applyPaymentCollectionModeChange,
  buildPaymentCollectionPayload,
  defaultPaymentCollectionForCreate,
  hydratePaymentCollectionFromPrestation,
  shouldWarnDepositOverBasePrice,
  validatePaymentCollectionForm,
} from '../utils/prestationPaymentCollectionForm.js';
import { getPrestationPlageBadge, summarizePrestationPlageDays } from '../utils/prestationDisponibilite.js';

const buildServicesState = (source) => ({
  livraison: source?.livraison ?? false,
  clickAndCollect: source?.clickAndCollect ?? false,
  paiementEnLigne: source?.paiementEnLigne ?? false,
  paiementSurPlace: source?.paiementSurPlace ?? false,
  priseRDV: source?.priseRDV ?? false,
  reservationTable: source?.reservationTable ?? false,
  priseCreneaux: source?.priseCreneaux ?? false,
  moduleCreneaux: source?.moduleCreneaux ?? source?.priseCreneaux ?? false,
});

const buildConfigState = (source) => ({
  pasGrilleMinutes: source?.pasGrilleMinutes ?? 15,
  dureesAutoriseesMinutes: Array.isArray(source?.dureesAutoriseesMinutes) && source.dureesAutoriseesMinutes.length
    ? source.dureesAutoriseesMinutes
    : [60, 90, 120],
  dureeRDVMinutes: source?.dureeRDVMinutes ?? 60,
  dureeTableMinutes: source?.dureeTableMinutes ?? 120,
  maxRDVParJour: source?.maxRDVParJour ?? 20,
  maxTablesParJour: source?.maxTablesParJour ?? 50,
  delaiAnnulationHeures: source?.delaiAnnulationHeures ?? 24,
  confirmationAutomatique: source?.confirmationAutomatique ?? true,
  rappelRDVHeures: source?.rappelRDVHeures ?? 2,
  joursReservationAvance: source?.joursReservationAvance ?? 60,
});

const DEFAULT_PRESTATION_COLOR = '#2563eb';

const JOUR_OPTIONS = [
  { value: 1, label: 'Lundi' },
  { value: 2, label: 'Mardi' },
  { value: 3, label: 'Mercredi' },
  { value: 4, label: 'Jeudi' },
  { value: 5, label: 'Vendredi' },
  { value: 6, label: 'Samedi' },
  { value: 0, label: 'Dimanche' },
];

const DEFAULT_CRENEAU_FORM = {
  type: 'RECURRENT',
  jourSemaine: 1,
  dateSpecifique: '',
  heureDebut: '09:00',
  heureFin: '10:00',
  // CAP-1 : héritage par défaut (capacite API = null). null ≠ override 1.
  capaciteMode: CAPACITE_MODE.HERITE,
  capaciteOverride: 1,
  actif: true,
};

const DEFAULT_OPTION_FORM = {
  nom: '',
  description: '',
  prixSupplement: '',
  ordre: 0,
  actif: true,
};

const MAX_PRESTATION_IMAGES = 6;

const getJourLabel = (value) => {
  const option = JOUR_OPTIONS.find((item) => item.value === value);
  return option ? option.label : 'Jour inconnu';
};

const formatDateForInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateLong = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const timeToMinutes = (value) => {
  if (!value) return 0;
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
};

const extractPrestationsList = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.prestations)) return response.prestations;
  if (Array.isArray(response)) return response;
  return [];
};

const enrichPrestationsWithCreneaux = async (list, apiService) => {
  const needsFetch = list.filter((p) => {
    const count = p._count?.creneaux ?? 0;
    if (count === 0) return false;
    const days = summarizePrestationPlageDays(p.creneaux);
    return !days;
  });
  if (needsFetch.length === 0) return list;

  const byId = new Map(list.map((p) => [p.id, { ...p }]));
  await Promise.all(
    needsFetch.map(async (p) => {
      try {
        const res = await apiService.getPrestationCreneaux(p.id);
        byId.set(p.id, { ...byId.get(p.id), creneaux: res?.creneaux || [] });
      } catch {
        /* badge fallback */
      }
    }),
  );
  return list.map((p) => byId.get(p.id) || p);
};

const ServicesManager = forwardRef(({ commercant, onUpdate, onAddPrestation, onSaveCommercantHoraires, savingHoraires = false, embeddedAddMode = false, loading = false }, ref) => {
  const [services, setServices] = useState(() => buildServicesState(commercant));

  const [config, setConfig] = useState(() => buildConfigState(commercant));
  const imageInputRef = useRef(null);

  useEffect(() => {
    setTypeFilter('');
  }, [commercant?.id]);

  useEffect(() => {
    const baseServices = buildServicesState(commercant);
    const autoEnableKeys = (getServicesByCategorie(commercant?.categorie)?.required || []).filter(
      (key) => ['priseRDV', 'priseCreneaux', 'reservationTable'].includes(key)
    );

    if (autoEnableKeys.length > 0) {
      autoEnableKeys.forEach((key) => {
        if (!baseServices[key]) {
          baseServices[key] = true;
        }
      });
    }

    setServices(baseServices);
    setConfig(buildConfigState(commercant));
  }, [commercant]);

  // État pour la gestion des prestations
  const [prestations, setPrestations] = useState([]);
  const [prestationsLoading, setPrestationsLoading] = useState(false);
  const [prestationsError, setPrestationsError] = useState(null);
  const [savingPrestation, setSavingPrestation] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  // État pour le filtre de type de prestation
  const [typeFilter, setTypeFilter] = useState('');
  const [showPrestationModal, setShowPrestationModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPrestation, setPreviewPrestation] = useState(null);
  const [showHorairesSubModal, setShowHorairesSubModal] = useState(false);
  const [editingPrestation, setEditingPrestation] = useState(null);
  const [prestationForm, setPrestationForm] = useState({
    nom: '',
    description: '',
    images: [],
    dureeMinutes: 60,
    prix: '',
    prixPromo: '',
    couleur: DEFAULT_PRESTATION_COLOR,
    maxParticipants: 1,
    equipementFourni: false,
    preparationRequis: '',
    typePrestation: 'CRENEAUX',
    tauxTVA: getDefaultTauxTVA(commercant?.categorie),
    instructeurId: null,
    dureesAutoriseesMinutes: [],
    tarifsDuree: [],
    ressourceId: null,
    reservationEnLigne: true,
    categorie: '',
    sousCategorie: '',
    necessitePersonnel: defaultNecessitePersonnelForCreate(),
    ...defaultPaymentCollectionForCreate(),
  });
  const [shopRessources, setShopRessources] = useState([]);
  const [instructeurs, setInstructeurs] = useState([]);
  const [instructeursLoading, setInstructeursLoading] = useState(false);
  const [creneaux, setCreneaux] = useState([]);
  const [creneauxLoading, setCreneauxLoading] = useState(false);
  const [creneauxError, setCreneauxError] = useState(null);
  const [creneauxFeedback, setCreneauxFeedback] = useState(null);
  const [creneauForm, setCreneauForm] = useState(DEFAULT_CRENEAU_FORM);
  const [editingCreneau, setEditingCreneau] = useState(null);
  const [savingCreneau, setSavingCreneau] = useState(false);
  const [savingBulkCreneaux, setSavingBulkCreneaux] = useState(false);
  const [deleteCreneauConfirmation, setDeleteCreneauConfirmation] = useState(null);
  const [creneauActionId, setCreneauActionId] = useState(null);
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState(null);
  const [optionsFeedback, setOptionsFeedback] = useState(null);
  const [optionForm, setOptionForm] = useState(DEFAULT_OPTION_FORM);
  const [editingOption, setEditingOption] = useState(null);
  const [optionActionId, setOptionActionId] = useState(null);
  const [deleteOptionConfirmation, setDeleteOptionConfirmation] = useState(null);
  const [imageDragActive, setImageDragActive] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  const fetchPrestations = useCallback(async () => {
    if (!commercant?.id) {
      setPrestations([]);
      return;
    }

    try {
      setPrestationsError(null);
      setPrestationsLoading(true);
      const response = await apiService.getPrestationsByCommercant(commercant.id, typeFilter, {
        pageSize: 200,
      });
      const rawList = extractPrestationsList(response);
      const list = await enrichPrestationsWithCreneaux(rawList, apiService);
      setPrestations(list);
    } catch (error) {
      console.error('Erreur lors du chargement des prestations:', error);
      setPrestations([]);
      setPrestationsError(
        error?.message || "Impossible de charger les prestations pour ce commerçant."
      );
    } finally {
      setPrestationsLoading(false);
    }
  }, [commercant?.id, typeFilter]);

  // Charger les prestations existantes
  useEffect(() => {
    fetchPrestations();
  }, [fetchPrestations]);

  const refreshShopRessources = useCallback(async () => {
    if (!commercant?.id || !(commercant.moduleCreneaux || commercant.priseCreneaux)) {
      setShopRessources([]);
      return;
    }
    try {
      const res = await apiService.getRessourcesByCommercant(commercant.id, { actif: true });
      setShopRessources(res?.ressources || []);
    } catch {
      setShopRessources([]);
    }
  }, [commercant?.id, commercant?.moduleCreneaux, commercant?.priseCreneaux]);

  useEffect(() => {
    refreshShopRessources();
  }, [refreshShopRessources]);

  const activePrestationType =
    prestationForm.typePrestation || getDefaultPrestationType(commercant);
  const prestationModalUi = useMemo(
    () => getPrestationModalUi(activePrestationType, commercant),
    [activePrestationType, commercant],
  );

  const equipementDetails = useMemo(
    () => parsePreparationRequis(prestationForm.preparationRequis),
    [prestationForm.preparationRequis],
  );

  const updateEquipementDetails = (patch) => {
    setPrestationForm((prev) => {
      const current = parsePreparationRequis(prev.preparationRequis);
      const next = { ...current, ...patch };
      const preparationRequis = serializePreparationRequis(next) || '';
      return {
        ...prev,
        preparationRequis,
        equipementFourni: hasEquipementFourni({
          equipementFourni: prev.equipementFourni,
          fourniGratuit: next.fourniGratuit,
          fourniPayant: next.fourniPayant,
        }),
      };
    });
  };

  const refreshInstructeurs = useCallback(async () => {
    if (!commercant?.id || !prestationModalUi.showInstructeur) {
      setInstructeurs([]);
      return;
    }
    try {
      setInstructeursLoading(true);
      const res = await apiService.getInstructeurs(commercant.id, { actif: true });
      setInstructeurs(res?.instructeurs || []);
    } catch {
      setInstructeurs([]);
    } finally {
      setInstructeursLoading(false);
    }
  }, [commercant?.id, prestationModalUi.showInstructeur]);

  useEffect(() => {
    refreshInstructeurs();
  }, [refreshInstructeurs]);

  const handleTypePrestationChange = (newType) => {
    setPrestationForm((prev) => {
      const patch = getPrestationTypeSwitchPatch(newType, prev, commercant);
      if (newType !== 'CRENEAUX') {
        patch.dureesAutoriseesMinutes = [];
        patch.tarifsDuree = [];
      }
      if (newType === 'RESERVATION' || newType === 'RENDEZ_VOUS' || !shouldShowRessourceLink(newType, commercant)) {
        patch.ressourceId = null;
      }
      return { ...prev, typePrestation: newType, ...patch };
    });
  };

  const handlePrestationFormChange = (field, value) => {
    let nextValue = value;

    if (typeof value === 'number' && Number.isNaN(value)) {
      nextValue = field === 'prixPromo' ? null : '';
    }

    if (field === 'paymentCollectionMode') {
      setPrestationForm((prev) => ({
        ...prev,
        ...applyPaymentCollectionModeChange(nextValue, prev.depositAmount),
      }));
      return;
    }

    setPrestationForm((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
  };

  const resetCreneauForm = () => {
    const inherited = defaultCreneauFormCapacite(prestationForm?.maxParticipants);
    setCreneauForm({
      ...DEFAULT_CRENEAU_FORM,
      capaciteMode: inherited.mode,
      capaciteOverride: inherited.overrideValue,
    });
    setEditingCreneau(null);
  };

const resetOptionForm = () => {
  setOptionForm(DEFAULT_OPTION_FORM);
  setEditingOption(null);
};

const handleOptionFieldChange = (field, value) => {
  setOptionForm((prev) => ({
    ...prev,
    [field]: value,
  }));
};

  const handleCreneauFieldChange = (field, value) => {
    setCreneauForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const loadCreneaux = useCallback(async (prestationId) => {
    if (!prestationId) {
      setCreneaux([]);
      return;
    }

    try {
      setCreneauxLoading(true);
      setCreneauxError(null);
      setCreneauxFeedback(null);
      const response = await apiService.getPrestationCreneaux(prestationId);
      setCreneaux(Array.isArray(response?.creneaux) ? response.creneaux : []);
    } catch (error) {
      console.error('Erreur lors du chargement des créneaux:', error);
      setCreneaux([]);
      setCreneauxError(error?.message || 'Impossible de charger les créneaux pour cette prestation.');
    } finally {
      setCreneauxLoading(false);
    }
  }, []);

  const loadOptions = useCallback(async (prestationId) => {
    if (!prestationId) {
      setOptions([]);
      return;
    }

    try {
      setOptionsLoading(true);
      setOptionsError(null);
      setOptionsFeedback(null);
      const response = await apiService.getPrestationOptions(prestationId);
      setOptions(Array.isArray(response?.options) ? response.options : []);
    } catch (error) {
      console.error('Erreur lors du chargement des variantes:', error);
      setOptions([]);
      setOptionsError(error?.message || 'Impossible de charger les variantes pour cette prestation.');
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  const closePrestationModal = () => {
    if (showHorairesSubModal) return;
    setShowPrestationModal(false);
    setEditingPrestation(null);
    setCreneaux([]);
    setCreneauxError(null);
    setCreneauxFeedback(null);
    setDeleteCreneauConfirmation(null);
    setCreneauActionId(null);
    resetCreneauForm();
  setOptions([]);
  setOptionsError(null);
  setOptionsFeedback(null);
  setOptionActionId(null);
  setDeleteOptionConfirmation(null);
  resetOptionForm();
  };

  const handleCreneauSubmit = async () => {
    if (!editingPrestation?.id) {
      setCreneauxError("Enregistrez d'abord la prestation pour ajouter des créneaux personnalisés.");
      return;
    }

    if (!creneauForm.heureDebut || !creneauForm.heureFin) {
      setCreneauxError('Veuillez indiquer une heure de début et une heure de fin.');
      return;
    }

    if (timeToMinutes(creneauForm.heureFin) <= timeToMinutes(creneauForm.heureDebut)) {
      setCreneauxError("L'heure de fin doit être postérieure à l'heure de début.");
      return;
    }

    if (creneauForm.type === 'EXCEPTIONNEL' && !creneauForm.dateSpecifique) {
      setCreneauxError('Veuillez sélectionner une date spécifique pour ce créneau exceptionnel.');
      return;
    }

    let capacitePayload;
    try {
      capacitePayload = buildCreneauCapacitePayload({
        mode: creneauForm.capaciteMode,
        overrideValue: creneauForm.capaciteOverride,
      });
    } catch {
      setCreneauxError('La capacité personnalisée doit être un entier d\'au moins 1 participant.');
      return;
    }

    const payload = {
      type: creneauForm.type,
      heureDebut: creneauForm.heureDebut,
      heureFin: creneauForm.heureFin,
      capacite: capacitePayload,
      actif: Boolean(creneauForm.actif),
    };

    if (creneauForm.type === 'RECURRENT') {
      payload.jourSemaine = Number(creneauForm.jourSemaine);
    } else {
      payload.dateSpecifique = creneauForm.dateSpecifique;
    }

    try {
      setSavingCreneau(true);
      setCreneauxError(null);
      setCreneauxFeedback(null);

      if (editingCreneau) {
        await apiService.updatePrestationCreneau(editingPrestation.id, editingCreneau.id, payload);
        setCreneauxFeedback('Créneau mis à jour avec succès.');
      } else {
        await apiService.createPrestationCreneau(editingPrestation.id, payload);
        setCreneauxFeedback('Créneau ajouté avec succès.');
      }

      await loadCreneaux(editingPrestation.id);
      await fetchPrestations();
      resetCreneauForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du créneau:', error);
      setCreneauxError(error?.message || "Impossible d'enregistrer le créneau.");
    } finally {
      setSavingCreneau(false);
    }
  };

  const applyPlagesBatch = async (plages, { replace = false } = {}) => {
    if (!editingPrestation?.id || !plages.length) return;

    try {
      setSavingBulkCreneaux(true);
      setCreneauxError(null);
      setCreneauxFeedback(null);

      if (replace && creneaux.length > 0) {
        for (const c of creneaux) {
          await apiService.deletePrestationCreneau(editingPrestation.id, c.id);
        }
      }

      for (const plage of plages) {
        await apiService.createPrestationCreneau(editingPrestation.id, {
          ...plage,
          // CAP-1 : bulk sans personnalisation → héritage (null), pas de copie figée
          capacite: null,
        });
      }

      await loadCreneaux(editingPrestation.id);
      await fetchPrestations();
      setCreneauxFeedback(
        `${plages.length} plage${plages.length > 1 ? 's' : ''} enregistrée${plages.length > 1 ? 's' : ''}. Les créneaux réservables sont générés automatiquement.`,
      );
    } catch (error) {
      console.error('Erreur lors de l\'application des plages:', error);
      setCreneauxError(error?.message || 'Impossible d\'enregistrer les plages horaires.');
    } finally {
      setSavingBulkCreneaux(false);
    }
  };

  const handleBulkApplyPlages = async ({ jours, heureDebut, heureFin, replace, dureeMinutes: bulkDuree }) => {
    const nextDuree = Number(bulkDuree || prestationForm.dureeMinutes) || 60;
    if (nextDuree !== Number(prestationForm.dureeMinutes)) {
      setPrestationForm((prev) => ({ ...prev, dureeMinutes: nextDuree }));
      if (editingPrestation?.id) {
        try {
          await apiService.updatePrestation(editingPrestation.id, { dureeMinutes: nextDuree });
        } catch (error) {
          console.error('Erreur sync durée prestation:', error);
          setCreneauxError(error?.message || 'Impossible de mettre à jour la durée de la prestation.');
          return;
        }
      }
    }
    const plages = (jours || []).map((jourSemaine) => ({
      type: 'RECURRENT',
      jourSemaine,
      heureDebut,
      heureFin,
      actif: true,
    }));
    await applyPlagesBatch(plages, { replace });
  };

  const handleCopyFromBoutiqueHoraires = async () => {
    const plages = buildPlagesFromCommercantHoraires(commercant?.horaires);
    if (!plages.length) {
      setCreneauxError('Aucun horaire boutique à copier. Configurez d\'abord les horaires d\'ouverture.');
      return;
    }
    await applyPlagesBatch(plages, { replace: true });
  };

  const handleClearAllPlages = async () => {
    if (!editingPrestation?.id || creneaux.length === 0) return;

    try {
      setSavingBulkCreneaux(true);
      setCreneauxError(null);
      for (const c of creneaux) {
        await apiService.deletePrestationCreneau(editingPrestation.id, c.id);
      }
      await loadCreneaux(editingPrestation.id);
      setCreneauxFeedback('Plages supprimées — les horaires de la boutique s\'appliquent à nouveau.');
    } catch (error) {
      console.error('Erreur suppression plages:', error);
      setCreneauxError(error?.message || 'Impossible de supprimer les plages.');
    } finally {
      setSavingBulkCreneaux(false);
    }
  };

  const handleEditCreneau = (creneau) => {
    setEditingCreneau(creneau);
    const capaciteState = creneauFormCapaciteFromStored(creneau, prestationForm?.maxParticipants);
    setCreneauForm({
      type: creneau.type,
      jourSemaine: creneau.jourSemaine ?? 1,
      dateSpecifique: formatDateForInput(creneau.dateSpecifique),
      heureDebut: creneau.heureDebut,
      heureFin: creneau.heureFin,
      capaciteMode: capaciteState.mode,
      capaciteOverride: capaciteState.overrideValue,
      actif: creneau.actif ?? true,
    });
  };

  const handleCancelCreneauEdit = () => {
    resetCreneauForm();
  };

  const handleToggleCreneau = async (creneau) => {
    if (!editingPrestation?.id) return;

    const payload = {
      type: creneau.type,
      heureDebut: creneau.heureDebut,
      heureFin: creneau.heureFin,
      // CAP-1 : préserver null (héritage) — ne pas coercer en 1
      capacite: creneau.capacite == null ? null : creneau.capacite,
      actif: !creneau.actif,
    };

    if (creneau.type === 'RECURRENT') {
      payload.jourSemaine = creneau.jourSemaine;
    } else {
      payload.dateSpecifique = formatDateForInput(creneau.dateSpecifique);
    }

    try {
      setCreneauActionId(creneau.id);
      setCreneauxError(null);
      setCreneauxFeedback(null);
      await apiService.updatePrestationCreneau(editingPrestation.id, creneau.id, payload);
      await loadCreneaux(editingPrestation.id);
      await fetchPrestations();
      setCreneauxFeedback(`Créneau ${creneau.actif ? 'désactivé' : 'activé'} avec succès.`);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut du créneau:', error);
      setCreneauxError(error?.message || 'Impossible de mettre à jour le créneau.');
    } finally {
      setCreneauActionId(null);
    }
  };

  const requestDeleteCreneau = (creneau) => {
    setDeleteCreneauConfirmation({
      id: creneau.id,
      title: 'Supprimer le créneau',
      description:
        creneau.type === 'RECURRENT'
          ? `Supprimer le créneau récurrent du ${getJourLabel(creneau.jourSemaine)} (${creneau.heureDebut} - ${creneau.heureFin}) ?`
          : `Supprimer le créneau du ${formatDateLong(creneau.dateSpecifique)} (${creneau.heureDebut} - ${creneau.heureFin}) ?`,
    });
  };

  const confirmDeleteCreneau = async () => {
    if (!editingPrestation?.id || !deleteCreneauConfirmation?.id) return;

    try {
      setCreneauxError(null);
      setCreneauxFeedback(null);
      setCreneauActionId(deleteCreneauConfirmation.id);
      await apiService.deletePrestationCreneau(editingPrestation.id, deleteCreneauConfirmation.id);
      setDeleteCreneauConfirmation(null);
      await loadCreneaux(editingPrestation.id);
      await fetchPrestations();
      setCreneauxFeedback('Créneau supprimé avec succès.');
    } catch (error) {
      console.error('Erreur lors de la suppression du créneau:', error);
      setCreneauxError(error?.message || 'Impossible de supprimer le créneau.');
    } finally {
      setCreneauActionId(null);
    }
  };

  const handleOptionSubmit = async () => {
    if (!editingPrestation?.id) {
      setOptionsError("Enregistrez d'abord la prestation pour ajouter des variantes.");
      return;
    }

    if (!optionForm.nom?.trim()) {
      setOptionsError('Le nom de la variante est requis.');
      return;
    }

    const payload = {
      nom: optionForm.nom.trim(),
      description: optionForm.description?.trim() || null,
      prixSupplement:
        optionForm.prixSupplement === '' || optionForm.prixSupplement === null
          ? null
          : Number(optionForm.prixSupplement),
      ordre:
        optionForm.ordre === '' || optionForm.ordre === null
          ? 0
          : Number.isNaN(Number(optionForm.ordre))
          ? 0
          : Number(optionForm.ordre),
      actif: Boolean(optionForm.actif),
    };

    if (
      payload.prixSupplement !== null &&
      (Number.isNaN(payload.prixSupplement) || !Number.isFinite(payload.prixSupplement))
    ) {
      setOptionsError('Le prix supplémentaire doit être un nombre valide.');
      return;
    }

    try {
      setOptionActionId(editingOption ? editingOption.id : 'create');
      setOptionsError(null);
      setOptionsFeedback(null);

      if (editingOption) {
        await apiService.updatePrestationOption(editingOption.id, payload);
        setOptionsFeedback('Variante mise à jour avec succès.');
      } else {
        await apiService.createPrestationOption(editingPrestation.id, payload);
        setOptionsFeedback('Variante ajoutée avec succès.');
      }

      await loadOptions(editingPrestation.id);
      await fetchPrestations();
      resetOptionForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la variante:', error);
      setOptionsError(error?.message || "Impossible d'enregistrer la variante.");
    } finally {
      setOptionActionId(null);
    }
  };

  const handleEditOption = (option) => {
    setEditingOption(option);
    setOptionForm({
      nom: option.nom,
      description: option.description || '',
      prixSupplement:
        option.prixSupplement === null || option.prixSupplement === undefined
          ? ''
          : option.prixSupplement,
      ordre: option.ordre ?? 0,
      actif: option.actif ?? true,
    });
  };

  const handleCancelOptionEdit = () => {
    resetOptionForm();
  };

  const handleToggleOption = async (option) => {
    if (!editingPrestation?.id) return;

    try {
      setOptionActionId(option.id);
      setOptionsError(null);
      setOptionsFeedback(null);
      await apiService.updatePrestationOption(option.id, { actif: !option.actif });
      await loadOptions(editingPrestation.id);
      await fetchPrestations();
      setOptionsFeedback(`Variante ${option.actif ? 'désactivée' : 'activée'} avec succès.`);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la variante:', error);
      setOptionsError(error?.message || 'Impossible de mettre à jour la variante.');
    } finally {
      setOptionActionId(null);
    }
  };

  const requestDeleteOption = (option) => {
    setDeleteOptionConfirmation({
      id: option.id,
      title: 'Supprimer la variante',
      description: `Supprimer la variante "${option.nom}" ?`,
    });
  };

  const confirmDeleteOption = async () => {
    if (!editingPrestation?.id || !deleteOptionConfirmation?.id) return;
    const optionId = deleteOptionConfirmation.id;

    try {
      setOptionActionId(optionId);
      setOptionsError(null);
      setOptionsFeedback(null);
      await apiService.deletePrestationOption(optionId);
      await loadOptions(editingPrestation.id);
      await fetchPrestations();
      setOptionsFeedback('Variante supprimée avec succès.');
      if (editingOption?.id === optionId) {
        resetOptionForm();
      }
      setDeleteOptionConfirmation(null);
    } catch (error) {
      console.error('Erreur lors de la suppression de la variante:', error);
      setOptionsError(error?.message || 'Impossible de supprimer la variante.');
    } finally {
      setOptionActionId(null);
    }
  };

  const resetPrestationModalAuxiliary = () => {
    setCreneaux([]);
    setCreneauxError(null);
    setCreneauxFeedback(null);
    setDeleteCreneauConfirmation(null);
    setCreneauActionId(null);
    resetCreneauForm();
    setOptions([]);
    setOptionsError(null);
    setOptionsFeedback(null);
    setOptionActionId(null);
    setDeleteOptionConfirmation(null);
    resetOptionForm();
    setImageUploading(false);
    setImageDragActive(false);
  };

  const handleAddPrestation = (opts = {}) => {
    const reservationEnLigne =
      opts.reservationEnLigne === undefined ? true : Boolean(opts.reservationEnLigne);
    setEditingPrestation(null);
    setPrestationForm({
      nom: '',
      description: '',
      images: [],
      dureeMinutes: 60,
      prix: '',
      prixPromo: '',
      couleur: DEFAULT_PRESTATION_COLOR,
      maxParticipants: 1,
      equipementFourni: false,
      preparationRequis: '',
      typePrestation: getDefaultPrestationType(commercant),
      tauxTVA: getDefaultTauxTVA(commercant?.categorie),
      instructeurId: null,
      dureesAutoriseesMinutes: [],
      tarifsDuree: [],
      ressourceId: null,
      reservationEnLigne,
      categorie: '',
      sousCategorie: '',
      necessitePersonnel: defaultNecessitePersonnelForCreate(),
      ...defaultPaymentCollectionForCreate(),
    });
    resetPrestationModalAuxiliary();
    setShowPrestationModal(true);
  };

  const handlePreviewPrestation = (prestation) => {
    if (!prestation?.id) return;
    setPreviewPrestation(prestation);
    setShowPreviewModal(true);
  };

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewPrestation(null);
  };

  const handleEditHorairesBoutique = () => {
    setShowHorairesSubModal(true);
  };

  const handleSaveHorairesBoutique = async (horairesFrontend) => {
    if (!onSaveCommercantHoraires) return;
    const saved = await onSaveCommercantHoraires(horairesFrontend);
    if (saved) setShowHorairesSubModal(false);
  };

  const handleEditPrestation = (prestation) => {
    setEditingPrestation(prestation);
    setPrestationForm({
      nom: prestation.nom,
      description: prestation.description || '',
      images: Array.isArray(prestation.images) ? prestation.images : [],
      dureeMinutes: prestation.dureeMinutes,
      prix: prestation.prix,
      prixPromo: prestation.prixPromo ?? '',
      couleur: prestation.couleur || DEFAULT_PRESTATION_COLOR,
      maxParticipants: prestation.maxParticipants,
      equipementFourni: prestation.equipementFourni,
      preparationRequis: prestation.preparationRequis || '',
      typePrestation: prestation.typePrestation || 'CRENEAUX',
      tauxTVA: prestation.tauxTVA ?? getDefaultTauxTVA(commercant?.categorie),
      instructeurId: prestation.instructeurId || null,
      dureesAutoriseesMinutes: Array.isArray(prestation.dureesAutoriseesMinutes) ? prestation.dureesAutoriseesMinutes : [],
      tarifsDuree: Array.isArray(prestation.tarifsDuree) ? prestation.tarifsDuree : [],
      ressourceId: prestation.ressourceId || null,
      reservationEnLigne: prestation.reservationEnLigne !== false,
      categorie: prestation.categorie || '',
      sousCategorie: prestation.sousCategorie || '',
      // null / undefined → Auto ; true → Oui ; false → Non (ne pas Boolean()).
      necessitePersonnel:
        prestation.necessitePersonnel === true
          ? true
          : prestation.necessitePersonnel === false
            ? false
            : null,
      ...hydratePaymentCollectionFromPrestation(prestation),
    });
    setCreneaux([]);
    setCreneauxError(null);
    setCreneauxFeedback(null);
    setDeleteCreneauConfirmation(null);
    setCreneauActionId(null);
    resetCreneauForm();
  setOptions([]);
  setOptionsError(null);
  setOptionsFeedback(null);
  setOptionActionId(null);
  setDeleteOptionConfirmation(null);
  resetOptionForm();
    setShowPrestationModal(true);
    loadCreneaux(prestation.id);
  loadOptions(prestation.id);
  setImageUploading(false);
  setImageDragActive(false);
  };

  useImperativeHandle(ref, () => ({
    openCreatePrestation: (opts) => {
      handleAddPrestation(opts || {});
    },
    openCreatePrestationAdvanced: (opts) => {
      handleAddPrestation(opts || {});
    },
    openEditPrestation: async (prestationId) => {
      // Chercher d'abord dans la liste locale (prestations CRENEAUX)
      let prestation = prestations.find(p => p.id === prestationId);
      
      // Si pas trouvée localement, charger depuis l'API (peut être RENDEZ_VOUS)
      if (!prestation) {
        try {
          setPrestationsLoading(true);
          const response = await apiService.getPrestationById(prestationId);
          prestation = response?.prestation;
        } catch (error) {
          console.error('Erreur lors du chargement de la prestation:', error);
          setPrestationsError('Impossible de charger la prestation pour modification.');
          return;
        } finally {
          setPrestationsLoading(false);
        }
      }
      
      if (prestation) {
        handleEditPrestation(prestation);
      } else {
        setPrestationsError('Prestation introuvable.');
      }
    }
  }));

  const handleSavePrestation = async () => {
    const prix = roundMoney(prestationForm.prix);

    if (!prestationForm.nom || prix === null || prix <= 0) {
      setPrestationsError("Veuillez indiquer au minimum un nom et un prix pour la prestation.");
      return;
    }

    const paymentValidation = validatePaymentCollectionForm(prestationForm);
    if (!paymentValidation.valid) {
      setPrestationsError(paymentValidation.error || "Montant d'acompte invalide.");
      return;
    }

    let paymentFields;
    try {
      paymentFields = buildPaymentCollectionPayload(prestationForm);
    } catch (err) {
      setPrestationsError(err?.message || "Montant d'acompte invalide.");
      return;
    }

    try {
      setSavingPrestation(true);
      setPrestationsError(null);

      const details = parsePreparationRequis(prestationForm.preparationRequis);

      const payload = {
        nom: prestationForm.nom.trim(),
        description: prestationForm.description?.trim() || null,
        images: prestationForm.images?.filter((img) => typeof img === 'string' && img.length > 0) || [],
        dureeMinutes: Number(prestationForm.dureeMinutes) || 60,
        prix,
        prixPromo:
          prestationForm.prixPromo !== null && prestationForm.prixPromo !== ''
            ? (() => {
                const promo = parseFloat(prestationForm.prixPromo);
                return Number.isNaN(promo) ? null : promo;
              })()
            : null,
        couleur: prestationForm.couleur,
        maxParticipants: Number(prestationForm.maxParticipants) || 1,
        equipementFourni: hasEquipementFourni({
          equipementFourni: prestationForm.equipementFourni,
          fourniGratuit: details.fourniGratuit,
          fourniPayant: details.fourniPayant,
        }),
        preparationRequis: serializePreparationRequis(details),
        typePrestation: prestationForm.typePrestation || 'CRENEAUX',
        tauxTVA: normalizeTauxTVAInput(prestationForm.tauxTVA, commercant?.categorie),
        instructeurId: prestationForm.instructeurId || null,
        dureesAutoriseesMinutes: prestationForm.dureesAutoriseesMinutes?.length
          ? prestationForm.dureesAutoriseesMinutes
          : undefined,
        tarifsDuree: prestationForm.tarifsDuree?.length ? prestationForm.tarifsDuree : null,
        ressourceId: prestationForm.ressourceId || null,
        reservationEnLigne: prestationForm.reservationEnLigne !== false,
        categorie: prestationForm.categorie?.trim() || null,
        sousCategorie: prestationForm.sousCategorie?.trim() || null,
        // Explicit null = AUTO (ne jamais omettre sur create/update complet du formulaire).
        necessitePersonnel:
          prestationForm.necessitePersonnel === true
            ? true
            : prestationForm.necessitePersonnel === false
              ? false
              : null,
        paymentCollectionMode: paymentFields.paymentCollectionMode,
        depositAmount: paymentFields.depositAmount,
        commercantId: commercant.id,
      };

      const oldType = editingPrestation?.typePrestation;
      const newType = prestationForm.typePrestation || 'CRENEAUX';
      const typeChanged = editingPrestation && oldType !== newType;

      if (editingPrestation) {
        await apiService.updatePrestation(editingPrestation.id, payload);
        
        // Si le type a changé, afficher un message informatif
        if (typeChanged) {
          console.log(`Type de prestation changé de ${oldType} à ${newType}. La prestation apparaîtra maintenant dans l'onglet correspondant.`);
        }
      } else {
        await apiService.createPrestation(payload);
      }

      // Recharger les prestations après modification/création
      await fetchPrestations();
      closePrestationModal();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la prestation:', error);
      setPrestationsError(error?.message || "Impossible d'enregistrer la prestation.");
    } finally {
      setSavingPrestation(false);
    }
  };

  const confirmDeletePrestation = async () => {
    if (!deleteConfirmation?.id) return;

    try {
      setPrestationsError(null);
      await apiService.deletePrestation(deleteConfirmation.id);
      setDeleteConfirmation(null);
      await fetchPrestations();
    } catch (error) {
      console.error('Erreur lors de la suppression de la prestation:', error);
      setPrestationsError(error?.message || 'La suppression a échoué.');
    }
  };

  const handleDeletePrestation = (prestation) => {
    setDeleteConfirmation({
      id: prestation.id,
      title: 'Supprimer la prestation',
      description: `Confirmez-vous la suppression définitive de « ${prestation.nom} » ?`,
    });
  };

  // Configuration des services selon la catégorie
  // NOTE: Aucun service n'est obligatoire - c'est le libre choix du commerçant
  const getServicesByCategorie = (categorie) => {
    const configs = {
      // Alimentation & Restauration
      'RESTAURANT': {
        required: [], // Aucun service obligatoire
        recommended: ['reservationTable', 'livraison', 'clickAndCollect', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      'CAFE': {
        required: [],
        recommended: ['paiementEnLigne', 'reservationTable', 'clickAndCollect', 'paiementSurPlace'],
        optional: ['livraison']
      },
      'PIZZERIA': {
        required: [],
        recommended: ['livraison', 'paiementEnLigne', 'clickAndCollect', 'paiementSurPlace'],
        optional: ['reservationTable']
      },
      'FAST_FOOD': {
        required: [],
        recommended: ['livraison', 'paiementEnLigne', 'clickAndCollect', 'paiementSurPlace'],
        optional: []
      },
      
      // Services & Professionnels
      'SERVICE': {
        required: [],
        recommended: ['priseCreneaux', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect']
      },
      'CONSULTANT': {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect']
      },
      'COMPTABILITE': {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect']
      },
      
      // Santé & Bien-être
      'PHARMACIE': {
        required: [],
        recommended: ['clickAndCollect', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['livraison', 'priseRDV']
      },
      'MEDECIN': {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      'DENTISTE': {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      'KINE': {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      
      // Beauté & Coiffure
      'COIFFURE': {
        required: [],
        recommended: ['priseRDV', 'clickAndCollect', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      'BARBIER': {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect']
      },
      'SALON_BEAUTE': {
        required: [],
        recommended: ['priseRDV', 'clickAndCollect', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      'SPA': {
        required: [],
        recommended: ['priseRDV', 'clickAndCollect', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      'MASSAGE': {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      
      // Sports & Loisirs
      'CENTRE_EQUESTRE': {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect']
      },
      'TENNIS': {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect']
      },
      'FITNESS': {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect']
      },
      'GOLF': {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect']
      },
      'PADEL': {
        required: [],
        recommended: ['priseCreneaux', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect']
      },
      'FOOT': {
        required: [],
        recommended: ['priseCreneaux', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect']
      },
      'MULTISPORT': {
        required: [],
        recommended: ['priseCreneaux', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect']
      },
      'KARTING': {
        required: [],
        recommended: ['priseCreneaux', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      'ESCALADE': {
        required: [],
        recommended: ['priseCreneaux', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      'LASER_GAME': {
        required: [],
        recommended: ['priseCreneaux', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      'ESCAPE_GAME': {
        required: [],
        recommended: ['priseCreneaux', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      'SALLE_SPORT': {
        required: [],
        recommended: ['priseRDV', 'priseCreneaux', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect']
      },
      'PISCINE': {
        required: [],
        recommended: ['priseRDV', 'priseCreneaux', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      'NAIL_BAR': {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect']
      },
      'BOULANGERIE': {
        required: [],
        recommended: ['clickAndCollect', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['livraison']
      },
      'CABINET_MEDICAL': {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: []
      },
      
      // Retail & Commerce
      'BOUTIQUE': {
        required: [],
        recommended: ['clickAndCollect', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['livraison']
      },
      'PRET_A_PORTER': {
        required: [],
        recommended: ['clickAndCollect', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['livraison']
      },
      
      // Par défaut
      'DEFAULT': {
        required: [], // Aucun service obligatoire
        recommended: ['paiementEnLigne', 'paiementSurPlace'],
        optional: ['livraison', 'clickAndCollect', 'priseRDV']
      }
    };

    return configs[categorie] || configs['DEFAULT'];
  };

  const serviceConfig = getServicesByCategorie(commercant?.categorie);

  const getServiceIcon = (serviceKey) => {
    const icons = {
      livraison: '🚚',
      clickAndCollect: '📦',
      paiementEnLigne: '💳',
      paiementSurPlace: '💰',
      priseRDV: '📅',
      reservationTable: '🍽️',
      priseCreneaux: '⏰'
    };
    return icons[serviceKey] || '⚙️';
  };

  const getServiceLabel = (serviceKey) => {
    const labels = {
      livraison: 'Service de livraison',
      clickAndCollect: 'Click & Collect',
      paiementEnLigne: 'Paiement en ligne (CB)',
      paiementSurPlace: 'Paiement sur place',
      priseRDV: 'Prise de rendez-vous',
      reservationTable: 'Réservation de table',
      priseCreneaux: 'Prise de créneaux'
    };
    return labels[serviceKey] || serviceKey;
  };

  const getServiceDescription = (serviceKey) => {
    const descriptions = {
      livraison: 'Livraison à domicile',
      clickAndCollect: 'Retrait sur place',
      paiementEnLigne: 'Accepter le paiement en ligne',
      paiementSurPlace: 'Paiement en espèces ou CB sur place',
      priseRDV: 'Permettre aux clients de prendre rendez-vous',
      reservationTable: 'Réservation de table en ligne',
      priseCreneaux: 'Prise de créneaux pour services'
    };
    return descriptions[serviceKey] || '';
  };

  const handleServiceToggle = (serviceKey) => {
    const next = !services[serviceKey];
    const newServices = { ...services, [serviceKey]: next };
    if (serviceKey === 'priseCreneaux') {
      newServices.moduleCreneaux = next;
      if (next) newServices.priseRDV = true;
    }
    setServices(newServices);
  };

  const handleConfigChange = (key, value) => {
    setConfig({ ...config, [key]: value });
  };

  const handleSave = () => {
    const dataToSave = {
      ...services,
      ...config,
      moduleCreneaux: Boolean(services.priseCreneaux || config.moduleCreneaux),
      priseCreneaux: services.priseCreneaux,
    };
    onUpdate(dataToSave);
  };

  const isServiceRequired = (serviceKey) => {
    return serviceConfig.required.includes(serviceKey);
  };

  const isServiceRecommended = (serviceKey) => {
    return serviceConfig.recommended.includes(serviceKey);
  };

  const getValidationErrors = () => {
    const errors = [];
    
    const coreServices = ['livraison', 'clickAndCollect', 'priseRDV', 'reservationTable', 'priseCreneaux'];
    const hasCoreService = coreServices.some(service => services[service]);

    if (!hasCoreService) {
      const labels = coreServices
        .filter(service => typeof services[service] !== 'undefined')
        .map(getServiceLabel)
        .join(', ');
      errors.push(`Activez au moins un service principal (${labels}).`);
    }

    serviceConfig.required.forEach(requiredService => {
      if (!services[requiredService]) {
        errors.push(`Le service "${getServiceLabel(requiredService)}" est obligatoire pour cette catégorie`);
      }
    });

    return errors;
  };

  const validationErrors = getValidationErrors();

  const addImagesFromFileList = async (fileList) => {
    if (!fileList || fileList.length === 0) {
      return;
    }
    const files = Array.from(fileList).filter((file) => file.type?.startsWith('image/'));
    if (files.length === 0) {
      return;
    }
    
    setImageUploading(true);
    try {
      // IMAGES-STANDARD: Upload vers Cloudinary uniquement (plus de base64)
      // Limiter le nombre d'images à uploader selon MAX_PRESTATION_IMAGES
      const currentImageCount = prestationForm.images?.length || 0;
      const remainingSlots = Math.max(0, MAX_PRESTATION_IMAGES - currentImageCount);
      const filesToUpload = files.slice(0, remainingSlots);
      
      if (filesToUpload.length === 0) {
        alert(`Maximum ${MAX_PRESTATION_IMAGES} images autorisées`);
        setImageUploading(false);
        return;
      }

      // Upload vers Cloudinary (retourne uniquement les URLs)
      const uploadedUrls = await uploadMultipleImagesToCloudinary(filesToUpload, 'prestations');
      
      // Ajouter les URLs au state
      setPrestationForm((prev) => {
        const nextImages = prev.images ? [...prev.images] : [];
        const newImages = [...nextImages, ...uploadedUrls].slice(0, MAX_PRESTATION_IMAGES);
        return {
          ...prev,
          images: newImages,
        };
      });
    } catch (error) {
      console.error('Erreur upload images:', error);
      alert('Erreur lors de l\'upload des images. Veuillez réessayer.');
    } finally {
      setImageUploading(false);
    }
  };

  const handleImageInputChange = (event) => {
    addImagesFromFileList(event.target.files);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleImageDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setImageDragActive(true);
  };

  const handleImageDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setImageDragActive(false);
  };

  const handleImageDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setImageDragActive(false);
    addImagesFromFileList(event.dataTransfer?.files);
  };

  const handleImageSelectClick = () => {
    imageInputRef.current?.click();
  };

  const removePrestationImage = (indexToRemove) => {
    setPrestationForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToRemove),
    }));
  };

  const setImageAsCover = (index) => {
    setPrestationForm((prev) => {
      if (index === 0) return prev;
      const nextImages = [...prev.images];
      const [selected] = nextImages.splice(index, 1);
      nextImages.unshift(selected);
      return {
        ...prev,
        images: nextImages,
      };
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Activités</h3>
            <p className="text-sm text-gray-500">
              Créez et gérez les activités réservables par vos clients.
            </p>
          </div>
        </div>
        {!embeddedAddMode && (
          <button
            type="button"
            onClick={handleAddPrestation}
            title="Créer une activité réservable"
            className="inline-flex shrink-0 items-center self-start px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Nouvelle activité
          </button>
        )}
      </div>

      {/* Gestion des prestations */}
      {/* Tous les types de prestations sont gérés ici avec un filtre par type */}
      <>
          <div className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
            <h4 className="text-sm font-semibold text-indigo-900 mb-1">
              Réservation en ligne — calendrier client
            </h4>
            <p className="text-xs text-indigo-800 mb-3">
              Jusqu&apos;à combien de jours à l&apos;avance vos clients peuvent réserver (ex. 60 ≈ 2 mois, 90 ≈ 3 mois).
              Ce réglage se trouve ici dans l&apos;onglet <strong>Prestations</strong>, en haut de la liste.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[140px]">
                <label className="text-xs font-medium text-gray-700 mb-1 block">Horizon (jours)</label>
                <input
                  type="number"
                  min={7}
                  max={365}
                  value={config.joursReservationAvance ?? 60}
                  onChange={(e) =>
                    handleConfigChange('joursReservationAvance', Math.min(365, Math.max(7, parseInt(e.target.value, 10) || 60)))
                  }
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                />
              </div>
              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                Enregistrer l&apos;horizon
              </button>
            </div>
          </div>

          <div className="mb-4">
            <h4 className="font-medium text-gray-900 mb-2">Filtrer par type</h4>
            <PrestationTypePicker
              mode="filter"
              value={typeFilter}
              onChange={setTypeFilter}
              commercant={commercant}
            />
          </div>

          {/* Messages d'erreur */}
          {prestationsError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {prestationsError}
            </div>
          )}

          {!prestationsLoading &&
            !embeddedAddMode &&
            (commercant?.prestations?.length ?? prestations.length) === 0 && (
              <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-5">
                <h4 className="text-base font-semibold text-blue-900">Créer ma première activité</h4>
                <p className="mt-1 text-sm text-blue-800">
                  Décrivez votre activité, ses tarifs et sa disponibilité — le formulaire s&apos;adapte à votre métier.
                </p>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={handleAddPrestation}
                    className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    <Plus className="mr-1.5 h-4 w-4" />
                    Nouvelle activité
                  </button>
                </div>
              </div>
            )}

          {/* Message informatif si aucune prestation pour le filtre actif */}
          {!prestationsLoading && prestations.length === 0 && (() => {
            const hiddenCount = typeFilter
              ? (commercant?.prestations || []).filter(
                  (p) => p.typePrestation && p.typePrestation !== typeFilter,
                ).length
              : 0;
            return (
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">
                    {typeFilter 
                      ? `Aucune prestation de type « ${getPrestationTypeLabel(typeFilter)} » configurée`
                      : 'Aucune prestation configurée'
                    }
                  </h4>
                  {hiddenCount > 0 && (
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-2">
                      {hiddenCount} prestation{hiddenCount > 1 ? 's' : ''} existe{hiddenCount > 1 ? 'nt' : ''} sous un autre type.
                      Choisissez <strong>« Tous les types »</strong> ou le bon type dans la liste ci-dessus.
                    </p>
                  )}
                  <p className="text-sm text-blue-800 mb-2">
                    {typeFilter
                      ? `Les prestations « ${getPrestationTypeLabel(typeFilter)} » correspondent à ce type de réservation.`
                      : 'Créez des prestations adaptées à votre activité : terrains en créneaux, rendez-vous ou tables.'}
                  </p>
                  <p className="text-sm text-blue-800">
                    Cliquez sur <strong>Nouvelle activité</strong> pour ouvrir le formulaire complet.
                  </p>
                </div>
              </div>
            </div>
            );
          })()}

          {/* Liste des prestations */}
          {prestationsLoading ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-500">
              <div className="mb-3 h-10 w-10 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
              <p>Chargement des prestations…</p>
            </div>
          ) : prestations.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {prestations.map((prestation) => (
                <div
                  key={prestation.id}
                  className="group rounded-lg border border-gray-200 p-4 transition-colors hover:border-gray-300"
                >
                {prestation.images && prestation.images.length > 0 && (
                  <div className="mb-3 overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
                    <img
                      src={cloudinaryDeliveryUrl(prestation.images[0], { width: CLOUDINARY_ADMIN_WIDTH.card }) || prestation.images[0]}
                      alt={`Visuel ${prestation.nom}`}
                      className="h-36 w-full object-cover"
                    />
                  </div>
                )}
                <div className="mb-3 flex items-start justify-between">
                  <div className="flex items-center space-x-2">
                    <div
                      className="h-4 w-4 rounded-full"
                      style={{ backgroundColor: prestation.couleur }}
                    />
                    <h5 className="font-medium text-gray-900">{prestation.nom}</h5>
                    {prestation.categorie && (
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
                        {prestation.categorie}
                        {prestation.sousCategorie ? ` › ${prestation.sousCategorie}` : ''}
                      </span>
                    )}
                    {prestation.reservationEnLigne !== false && (
                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                        Réservable
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {(() => {
                      const badge = getPrestationPlageBadge(prestation);
                      const badgeClass =
                        badge.variant === 'auto'
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-blue-50 text-blue-700';
                      return (
                        <span
                          className={`inline-flex max-w-[160px] items-center truncate rounded-full px-2 py-0.5 text-[10px] font-semibold ${badgeClass}`}
                          title={badge.title}
                        >
                          {badge.label}
                        </span>
                      );
                    })()}
                    <button
                      onClick={() => handlePreviewPrestation(prestation)}
                      className="rounded-md p-1 text-gray-400 transition hover:bg-purple-50 hover:text-purple-700"
                      title="Visualiser la prestation (parcours client)"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEditPrestation(prestation)}
                      className="rounded-md p-1 text-gray-400 transition hover:bg-blue-50 hover:text-blue-600"
                      title="Modifier la prestation"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePrestation(prestation)}
                      className="rounded-md p-1 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                      title="Supprimer la prestation"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                  {prestation.description && (
                    <p className="mb-3 text-sm text-gray-600">{prestation.description}</p>
                  )}

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-4 text-gray-500">
                      <span className="inline-flex items-center">
                        <Clock className="mr-1 h-4 w-4" />
                        {prestation.dureeMinutes} min
                      </span>
                      <span className="inline-flex items-center">
                        <Users className="mr-1 h-4 w-4" />
                        {prestation.maxParticipants}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">
                        {Number(prestation.prix ?? 0).toFixed(2)}€
                      </div>
                      {prestation.prixPromo && (
                        <div className="text-sm text-green-600">
                          Promo&nbsp;: {Number(prestation.prixPromo ?? 0).toFixed(2)}€
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <Calendar className="mx-auto mb-4 h-12 w-12 text-gray-300" />
              <p>Aucune activité configurée pour ce filtre</p>
              <p className="mt-1 text-sm">
                Cliquez sur <strong>Nouvelle activité</strong> pour commencer.
              </p>
              {!embeddedAddMode && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={handleAddPrestation}
                    className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Nouvelle activité
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Modal pour ajouter/modifier une prestation */}
          <Modal
            isOpen={showPrestationModal}
            onClose={closePrestationModal}
            closeOnBackdrop={!showHorairesSubModal}
            title={editingPrestation ? 'Modifier l’activité' : 'Nouvelle activité'}
            maxWidth="max-w-4xl"
          >
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 shadow-sm">
            <div className="rounded-t-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4 text-white">
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5" />
                <div>
                  <h3 className="text-lg font-semibold">{prestationModalUi.headerTitle}</h3>
                  <p className="text-xs text-blue-100">{prestationModalUi.headerSubtitle}</p>
                </div>
              </div>
            </div>
            <div className="space-y-4 px-5 py-6">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-emerald-950">Réservation en ligne</h4>
                    <p className="mt-1 text-xs text-emerald-900/80">
                      Lorsque cette option est activée, les clients peuvent choisir un créneau et
                      réserver cette prestation depuis le site.
                    </p>
                  </div>
                  <label className="inline-flex items-center gap-2 shrink-0 cursor-pointer">
                    <span className="text-xs font-medium text-emerald-900">
                      {prestationForm.reservationEnLigne !== false ? 'ON' : 'OFF'}
                    </span>
                    <input
                      type="checkbox"
                      className="h-5 w-9 cursor-pointer accent-emerald-600"
                      checked={prestationForm.reservationEnLigne !== false}
                      onChange={(e) =>
                        handlePrestationFormChange('reservationEnLigne', e.target.checked)
                      }
                    />
                  </label>
                </div>
                {prestationForm.reservationEnLigne === false && (
                  <p className="text-xs text-emerald-900/70">
                    Prestation visible sans bouton de réservation. Les créneaux et options déjà
                    configurés sont conservés et réactivables à tout moment.
                  </p>
                )}
              </div>

              <div className="rounded-xl border border-indigo-200 bg-indigo-50/70 p-4 space-y-3">
                <div>
                  <h4 className="text-sm font-semibold text-indigo-950">
                    {PAYMENT_COLLECTION_SECTION_TITLE}
                  </h4>
                  <p className="mt-1 text-xs text-indigo-900/80">
                    {PAYMENT_COLLECTION_SECTION_HELP}
                  </p>
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium text-indigo-950 mb-1">
                    Mode de collecte
                  </label>
                  <select
                    value={prestationForm.paymentCollectionMode || 'NONE'}
                    onChange={(e) =>
                      handlePrestationFormChange('paymentCollectionMode', e.target.value)
                    }
                    className="w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  >
                    {PAYMENT_COLLECTION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                {prestationForm.paymentCollectionMode === 'DEPOSIT_FIXED' && (
                  <div>
                    <label className="flex items-center text-sm font-medium text-indigo-950 mb-1">
                      {DEPOSIT_AMOUNT_LABEL}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={prestationForm.depositAmount}
                        onChange={(e) =>
                          handlePrestationFormChange('depositAmount', e.target.value)
                        }
                        className="w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 pl-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                        placeholder="Ex. 20"
                      />
                      <Euro className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-indigo-400" />
                    </div>
                    <p className="mt-1 text-xs text-indigo-900/70">{DEPOSIT_AMOUNT_HELP}</p>
                    {shouldWarnDepositOverBasePrice({
                      paymentCollectionMode: prestationForm.paymentCollectionMode,
                      depositAmount: prestationForm.depositAmount,
                      prix: prestationForm.prix,
                    }) && (
                      <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                        {DEPOSIT_OVER_BASE_PRICE_WARNING}
                      </p>
                    )}
                  </div>
                )}
                {prestationForm.reservationEnLigne === false &&
                  prestationForm.paymentCollectionMode &&
                  prestationForm.paymentCollectionMode !== 'NONE' && (
                    <p className="text-xs text-indigo-900/70">
                      {PAYMENT_WHEN_RESERVATION_OFFLINE_HELP}
                    </p>
                  )}
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <h4 className="mb-3 text-sm font-semibold text-gray-900">
                  Rubrique catalogue
                </h4>
                <p className="mb-3 text-xs text-gray-500">
                  Indépendant de la réservation en ligne. Utile pour spa, soins, coiffure…
                  Laissez vide pour un classement par terrain / surface (padel, loisirs).
                </p>
                <CatalogueRubriqueSelect
                  commercantId={commercant?.id}
                  categorie={prestationForm.categorie || ''}
                  sousCategorie={prestationForm.sousCategorie || ''}
                  onChange={({ categorie, sousCategorie }) => {
                    setPrestationForm((prev) => ({
                      ...prev,
                      categorie: categorie || '',
                      sousCategorie: sousCategorie || '',
                    }));
                  }}
                />
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    {prestationModalUi.nomLabel}
                  </label>
                  <input
                    type="text"
                    value={prestationForm.nom}
                    onChange={(e) => handlePrestationFormChange('nom', e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    placeholder={prestationModalUi.nomPlaceholder}
                  />
                </div>

                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    Type de prestation *
                  </label>
                  <PrestationTypePicker
                    mode="form"
                    value={activePrestationType}
                    onChange={handleTypePrestationChange}
                    commercant={commercant}
                  />
                </div>

                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    Personnel nécessaire
                  </label>
                  <select
                    value={necessitePersonnelToSelectValue(prestationForm.necessitePersonnel)}
                    onChange={(e) =>
                      handlePrestationFormChange(
                        'necessitePersonnel',
                        necessitePersonnelFromSelectValue(e.target.value)
                      )
                    }
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  >
                    {NECESSITE_PERSONNEL_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">{NECESSITE_PERSONNEL_HELP}</p>
                </div>

                <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
                  <p className="text-sm font-medium text-indigo-900">
                    {prestationModalUi.typeIcon} Mode « {prestationModalUi.typeLabel} »
                  </p>
                  <p className="mt-1 text-xs text-indigo-800">
                    {prestationModalUi.typeHint}
                    {prestationModalUi.showMultiDurees
                      ? ' — Durées multiples, terrain lié (padel/tennis) et tarifs par durée ci-dessous.'
                      : ' — Durée, prix et disponibilité automatique via vos horaires boutique.'}
                  </p>
                </div>

                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={prestationForm.description}
                    onChange={(e) => handlePrestationFormChange('description', e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    rows="3"
                    placeholder={prestationModalUi.descriptionPlaceholder}
                  />
                </div>

                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    Visuels de la prestation
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    ref={imageInputRef}
                    onChange={handleImageInputChange}
                  />
                  <div className="space-y-4">
                    {prestationForm.images.length > 0 && (
                      <div className="grid gap-3 sm:grid-cols-2">
                        {prestationForm.images.map((img, index) => (
                          <div key={index} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                            <div className="aspect-video overflow-hidden rounded-md bg-gray-100">
                              <img src={img} alt={`Visuel ${index + 1}`} className="h-full w-full object-cover" />
                            </div>
                            <div className="mt-3 flex items-center justify-between">
                              <span className="text-xs font-semibold text-gray-500">
                                {index === 0 ? 'Image principale' : 'Image supplémentaire'}
                              </span>
                              <div className="flex items-center gap-2">
                                {index !== 0 && (
                                  <button
                                    type="button"
                                    onClick={() => setImageAsCover(index)}
                                    className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-100"
                                  >
                                    Mettre en couverture
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removePrestationImage(index)}
                                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-100"
                                >
                                  Supprimer
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div
                      onDrop={handleImageDrop}
                      onDragOver={handleImageDragOver}
                      onDragLeave={handleImageDragLeave}
                      className={`relative flex min-h-[160px] flex-col items-center justify-center rounded-xl border-2 border-dashed transition ${
                        imageDragActive
                          ? 'border-primary-500 bg-primary/10'
                          : 'border-gray-200 hover:border-primary/30 bg-gray-50'
                      }`}
                    >
                      <div className="flex flex-col items-center space-y-3 px-6 py-8 text-center">
                        <div className="text-sm text-gray-500">
                          Glissez-déposez vos images ou
                        </div>
                        <button
                          type="button"
                          onClick={handleImageSelectClick}
                          className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow hover:bg-primary-dark"
                        >
                          Sélectionner des images
                        </button>
                        <p className="text-xs text-gray-400">
                          {prestationForm.images.length}/{MAX_PRESTATION_IMAGES} images · JPG ou PNG · &lt; 3&nbsp;Mo
                        </p>
                      </div>
                      {imageUploading && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/80">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary"></div>
                            <span>Chargement en cours…</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="flex items-center space-x-2 border-b border-gray-100 px-5 py-4">
                <Clock className="h-5 w-5 text-indigo-500" />
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{prestationModalUi.durationSectionTitle}</h3>
                  <p className="text-xs text-gray-500">
                    {prestationModalUi.durationSectionSubtitle}
                  </p>
                </div>
              </div>
              <div className="space-y-4 px-5 py-5">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">{prestationModalUi.dureeLabel}</label>
                  <input
                    type="number"
                    value={prestationForm.dureeMinutes}
                    onChange={(e) => handlePrestationFormChange('dureeMinutes', parseInt(e.target.value, 10) || 0)}
                    min="15"
                    step="15"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">{prestationModalUi.capacityLabel}</label>
                  <input
                    type="number"
                    value={prestationForm.maxParticipants}
                    onChange={(e) => handlePrestationFormChange('maxParticipants', parseInt(e.target.value, 10) || 1)}
                    min="1"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {prestationModalUi.capacityHint}
                  </p>
                </div>
                {prestationModalUi.showMultiDurees && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Durées autorisées (optionnel)</label>
                      <p className="text-xs text-gray-500 mb-2">
                        Vide = uniquement la durée par défaut ci-dessus. Cochez des boutons pour proposer plusieurs durées.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {DUREE_PRESETS.map((m) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => {
                              const set = new Set(prestationForm.dureesAutoriseesMinutes || []);
                              if (set.has(m)) set.delete(m);
                              else set.add(m);
                              handlePrestationFormChange('dureesAutoriseesMinutes', [...set].sort((a, b) => a - b));
                            }}
                            className={`px-2 py-1 rounded-full text-xs border ${
                              (prestationForm.dureesAutoriseesMinutes || []).includes(m)
                                ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'border-gray-300'
                            }`}
                          >
                            {m} min
                          </button>
                        ))}
                      </div>
                      {(() => {
                        const { list, label, hint } = describeDureesSite(commercant, prestationForm);
                        return (
                          <p className="mt-2 text-xs text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2">
                            <strong>{label} :</strong> {list.join(', ')} min
                            {hint ? <span className="block mt-1 text-indigo-600">{hint}</span> : null}
                          </p>
                        );
                      })()}
                    </div>
                    {prestationModalUi.showTarifsDuree && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Prix TTC par durée (optionnel)</label>
                      <p className="text-xs text-gray-500 mb-2">Sinon prorata depuis le prix et la durée par défaut.</p>
                      {(prestationForm.tarifsDuree || []).map((row, idx) => (
                        <div key={idx} className="flex gap-2 mb-2">
                          <input
                            type="number"
                            min="15"
                            step="15"
                            placeholder="Minutes"
                            value={row.minutes}
                            onChange={(e) => {
                              const next = [...(prestationForm.tarifsDuree || [])];
                              next[idx] = { ...next[idx], minutes: parseInt(e.target.value, 10) || 0 };
                              handlePrestationFormChange('tarifsDuree', next);
                            }}
                            className="w-24 rounded-lg border border-gray-200 px-2 py-1 text-sm"
                          />
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Prix TTC €"
                            value={row.prix}
                            onChange={(e) => {
                              const next = [...(prestationForm.tarifsDuree || [])];
                              next[idx] = { ...next[idx], prix: parseFloat(e.target.value) || 0 };
                              handlePrestationFormChange('tarifsDuree', next);
                            }}
                            className="flex-1 rounded-lg border border-gray-200 px-2 py-1 text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const next = (prestationForm.tarifsDuree || []).filter((_, i) => i !== idx);
                              handlePrestationFormChange('tarifsDuree', next);
                            }}
                            className="text-red-500 text-sm"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          handlePrestationFormChange('tarifsDuree', [
                            ...(prestationForm.tarifsDuree || []),
                            { minutes: prestationForm.dureeMinutes || 60, prix: parseFloat(prestationForm.prix) || 0 },
                          ]);
                        }}
                        className="text-sm text-indigo-600"
                      >
                        + Ajouter un tarif
                      </button>
                    </div>
                    )}
                  </>
                )}
                {prestationModalUi.showRessourceLink && shopRessources.length > 0 && (() => {
                  const spaceLabels = getSpaceUiConfig(commercant?.categorie);
                  return (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        {prestationModalUi.ressourceLabel || `${spaceLabels.spaceSingular} lié à cette activité`}
                      </label>
                      <select
                        value={prestationForm.ressourceId || ''}
                        onChange={(e) =>
                          handlePrestationFormChange('ressourceId', e.target.value || null)
                        }
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      >
                        <option value="">— Aucun (choix libre sur le site) —</option>
                        {shopRessources.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.nom}
                            {r.surface ? ` (${r.surface})` : ''}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        {prestationModalUi.ressourceHint ||
                          `${shopRessources.length} espace${shopRessources.length > 1 ? 's' : ''} configuré${shopRessources.length > 1 ? 's' : ''}.`}
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="flex items-center space-x-2 border-b border-gray-100 px-5 py-4">
                <Euro className="h-5 w-5 text-indigo-500" />
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Tarification</h3>
                  <p className="text-xs text-gray-500">Prix public TTC et éventuelle promotion</p>
                </div>
              </div>
              <div className="space-y-4 px-5 py-5">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Prix (€)*</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={prestationForm.prix}
                      onChange={(e) => handlePrestationFormChange('prix', e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      placeholder={prestationModalUi.prixPlaceholder}
                    />
                    <Euro className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Prix promotionnel (€)</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={prestationForm.prixPromo}
                      onChange={(e) => handlePrestationFormChange('prixPromo', e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      placeholder="Optionnel"
                    />
                    <Tag className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Renseignez uniquement si une remise temporaire est appliquée.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Taux de TVA (%)</label>
                  <select
                    value={prestationForm.tauxTVA}
                    onChange={(e) =>
                      handlePrestationFormChange('tauxTVA', parseFloat(e.target.value))
                    }
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  >
                    {TVA_RATE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Prix en TTC. Par défaut :{' '}
                    {isRestaurationCategorie(commercant?.categorie) ? '5,5 % (restauration)' : '20 % (taux normal)'}.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {prestationModalUi.showEquipement && (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center space-x-2 border-b border-gray-100 px-5 py-4">
              <Info className="h-5 w-5 text-indigo-500" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {prestationModalUi.preparationTitle || 'Préparation client'}
                </h3>
                <p className="text-xs text-gray-500">
                  {prestationModalUi.preparationSubtitle || 'Matériel et consignes avant la séance'}
                </p>
              </div>
            </div>
            <div className="space-y-4 px-5 py-5">
              <PrestationEquipementSection
                categorie={commercant?.categorie}
                equipementFourni={prestationForm.equipementFourni}
                onEquipementFourniChange={(checked) =>
                  handlePrestationFormChange('equipementFourni', checked)
                }
                equipementRequis={equipementDetails.requis}
                onEquipementRequisChange={(items) => updateEquipementDetails({ requis: items })}
                equipementFourniGratuit={equipementDetails.fourniGratuit}
                onEquipementFourniGratuitChange={(items) =>
                  updateEquipementDetails({ fourniGratuit: items })
                }
                equipementFourniPayant={equipementDetails.fourniPayant}
                onEquipementFourniPayantChange={(items) =>
                  updateEquipementDetails({ fourniPayant: items })
                }
                preparationInstructions={equipementDetails.instructions}
                onPreparationInstructionsChange={(value) =>
                  updateEquipementDetails({ instructions: value })
                }
              />

              {prestationModalUi.showInstructeur && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {prestationModalUi.instructeurAssignLabel || 'Qui réalise cette prestation ?'}
                    <Info className="h-3 w-3 ml-1 text-gray-400 inline" title="Assigner un praticien à cette prestation" />
                  </label>
                  <select
                    value={prestationForm.instructeurId || ''}
                    onChange={(e) => handlePrestationFormChange('instructeurId', e.target.value || null)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    disabled={instructeursLoading}
                  >
                    <option value="">Non assigné</option>
                    {instructeurs.map((instructeur) => (
                      <option key={instructeur.id} value={instructeur.id}>
                        {formatInstructeurName(instructeur)}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {prestationModalUi.instructeurAssignHint || 'Optionnel. Laissez vide si la prestation n’est pas liée à une personne précise.'}
                  </p>
                  {!instructeursLoading && instructeurs.length === 0 && (
                    <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      {prestationModalUi.instructeurEmptyHint || 'Aucun praticien enregistré.'}{' '}
                      <Link to="/equipe" className="font-semibold underline">
                        Ajouter un praticien
                      </Link>
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-start space-x-2 rounded-lg bg-blue-50 px-4 py-3">
                <Info className="mt-0.5 h-4 w-4 text-blue-500" />
                <p className="text-xs text-blue-800">{prestationModalUi.footerHint}</p>
              </div>
            </div>
          </div>
          )}

          {!prestationModalUi.showEquipement && prestationModalUi.showInstructeur && (
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm px-5 py-5">
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                {prestationModalUi.instructeurAssignLabel || 'Qui réalise cette prestation ?'}
              </label>
              <select
                value={prestationForm.instructeurId || ''}
                onChange={(e) => handlePrestationFormChange('instructeurId', e.target.value || null)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                disabled={instructeursLoading}
              >
                <option value="">Non assigné</option>
                {instructeurs.map((instructeur) => (
                  <option key={instructeur.id} value={instructeur.id}>
                    {formatInstructeurName(instructeur)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {prestationModalUi.showCreneaux && (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center space-x-2 border-b border-gray-100 px-5 py-4">
              <CalendarDays className="h-5 w-5 text-indigo-500" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900">{prestationModalUi.creneauxTitle}</h3>
                <p className="text-xs text-gray-500">{prestationModalUi.creneauxSubtitle}</p>
              </div>
            </div>
            <div className="px-5 py-5">
              {!editingPrestation ? (
                <p className="text-sm text-gray-500">Enregistrez la prestation pour configurer la disponibilité.</p>
              ) : (
                <div className="space-y-6">
                  {prestationModalUi.showCreneauxQuickSetup && (
                    <PrestationDisponibiliteSection
                      typePrestation={activePrestationType}
                      commercant={commercant}
                      dureeMinutes={prestationForm.dureeMinutes}
                      maxParticipants={prestationForm.maxParticipants}
                      editingPrestation={editingPrestation}
                      creneaux={creneaux}
                      creneauxLoading={creneauxLoading}
                      creneauxError={creneauxError}
                      creneauxFeedback={creneauxFeedback}
                      savingBulk={savingBulkCreneaux}
                      onBulkApplyPlages={handleBulkApplyPlages}
                      onCopyFromBoutiqueHoraires={handleCopyFromBoutiqueHoraires}
                      onClearAllPlages={handleClearAllPlages}
                      onEditHorairesBoutique={handleEditHorairesBoutique}
                      onDeleteCreneau={requestDeleteCreneau}
                      onDureeMinutesChange={(value) => handlePrestationFormChange('dureeMinutes', value)}
                    />
                  )}

                  <div className={prestationModalUi.showCreneauxQuickSetup ? 'border-t border-gray-200 pt-6' : ''}>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">
                      {prestationModalUi.showCreneauxQuickSetup
                        ? 'Ajout manuel — créneau par créneau'
                        : 'Créneaux personnalisés'}
                    </h4>
                    <p className="text-xs text-gray-500 mb-4">
                      Pour une date précise, une plage atypique ou une capacité différente sur un jour donné.
                    </p>
                    <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Type de créneau*</label>
                    <select
                      value={creneauForm.type}
                      onChange={(e) => handleCreneauFieldChange('type', e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    >
                      <option value="RECURRENT">Créneau récurrent (hebdomadaire)</option>
                      <option value="EXCEPTIONNEL">Créneau exceptionnel (date unique)</option>
                    </select>
                  </div>

                  {creneauForm.type === 'RECURRENT' ? (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Jour de la semaine*</label>
                      <select
                        value={creneauForm.jourSemaine}
                        onChange={(e) => handleCreneauFieldChange('jourSemaine', Number(e.target.value))}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      >
                        {JOUR_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Date spécifique*</label>
                      <input
                        type="date"
                        value={creneauForm.dateSpecifique}
                        onChange={(e) => handleCreneauFieldChange('dateSpecifique', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Heure de début*</label>
                    <input
                      type="time"
                      value={creneauForm.heureDebut}
                      onChange={(e) => handleCreneauFieldChange('heureDebut', e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Heure de fin*</label>
                    <input
                      type="time"
                      value={creneauForm.heureFin}
                      onChange={(e) => handleCreneauFieldChange('heureFin', e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Capacité de ce créneau
                    </label>
                    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="creneau-capacite-mode"
                          checked={creneauForm.capaciteMode === CAPACITE_MODE.HERITE}
                          onChange={() => handleCreneauFieldChange('capaciteMode', CAPACITE_MODE.HERITE)}
                          className="mt-1 h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-800">
                          Utiliser la capacité de la prestation
                          <span className="block text-xs text-gray-500 mt-0.5">
                            Hérite de la prestation :{' '}
                            {Math.max(1, Number(prestationForm.maxParticipants) || 1)} participants
                          </span>
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="creneau-capacite-mode"
                          checked={creneauForm.capaciteMode === CAPACITE_MODE.OVERRIDE}
                          onChange={() => handleCreneauFieldChange('capaciteMode', CAPACITE_MODE.OVERRIDE)}
                          className="mt-1 h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-800 flex-1">
                          Personnaliser
                          <span className="block text-xs text-gray-500 mt-0.5 mb-2">
                            Override explicite (y compris 1 participant) — distinct de l&apos;héritage.
                          </span>
                          <input
                            type="number"
                            value={creneauForm.capaciteOverride}
                            onChange={(e) =>
                              handleCreneauFieldChange(
                                'capaciteOverride',
                                Math.max(1, Number(e.target.value) || 1),
                              )
                            }
                            min="1"
                            disabled={creneauForm.capaciteMode !== CAPACITE_MODE.OVERRIDE}
                            className="w-full max-w-[8rem] rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-gray-100 disabled:text-gray-400"
                          />
                          <span className="ml-2 text-xs text-gray-500">
                            {creneauForm.capaciteMode === CAPACITE_MODE.OVERRIDE
                              ? `${creneauForm.capaciteOverride} participant${Number(creneauForm.capaciteOverride) > 1 ? 's' : ''} maximum`
                              : 'participants'}
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={creneauForm.actif}
                      onChange={(e) => handleCreneauFieldChange('actif', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Créneau actif</span>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3">
                  {editingCreneau && (
                    <button
                      type="button"
                      onClick={handleCancelCreneauEdit}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Annuler la modification
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleCreneauSubmit}
                    disabled={savingCreneau}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {savingCreneau && (
                      <span className="mr-2 inline-flex h-4 w-4 items-center justify-center">
                        <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      </span>
                    )}
                    {editingCreneau ? 'Mettre à jour le créneau' : 'Ajouter le créneau'}
                  </button>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900">Créneaux planifiés</h4>
                  {creneauxLoading ? (
                    <div className="flex items-center justify-center py-6 text-gray-500">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600 mr-3" />
                      Chargement des créneaux…
                    </div>
                  ) : creneaux.length > 0 ? (
                    <div className="space-y-3">
                      {creneaux.map((creneau) => (
                        <div
                          key={creneau.id}
                          className="flex flex-col gap-4 rounded-lg border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
                        >
                          <div>
                            <div className="flex items-center space-x-2">
                              {creneau.type === 'RECURRENT' ? (
                                <Repeat className="h-4 w-4 text-indigo-500" />
                              ) : (
                                <CalendarDays className="h-4 w-4 text-indigo-500" />
                              )}
                              <span className="text-sm font-medium text-gray-900 capitalize">
                                {creneau.type === 'RECURRENT'
                                  ? getJourLabel(creneau.jourSemaine)
                                  : formatDateLong(creneau.dateSpecifique)}
                              </span>
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                  creneau.actif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {creneau.actif ? 'Actif' : 'Inactif'}
                              </span>
                            </div>
                            <p className="mt-2 text-xs text-gray-600 md:text-sm">
                              {creneau.heureDebut} – {creneau.heureFin} •{' '}
                              {formatCreneauCapaciteListLabel(
                                creneau.capacite,
                                prestationForm.maxParticipants,
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleToggleCreneau(creneau)}
                              disabled={creneauActionId === creneau.id}
                              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 md:text-sm"
                            >
                              {creneau.actif ? 'Désactiver' : 'Activer'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEditCreneau(creneau)}
                              className="rounded-md border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 md:text-sm"
                            >
                              Modifier
                            </button>
                            <button
                              type="button"
                              onClick={() => requestDeleteCreneau(creneau)}
                              className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 md:text-sm"
                            >
                              Supprimer
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                      Aucun créneau défini pour l'instant. Ajoutez un premier créneau ci-dessus.
                    </div>
                  )}
                    </div>
                  </div>
                </div>
                </div>
              )}
            </div>
          </div>
          )}

          {prestationModalUi.showOptions && (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center space-x-2 border-b border-gray-100 px-5 py-4">
              <Tag className="h-5 w-5 text-indigo-500" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Variantes & options</h3>
                <p className="text-xs text-gray-500">Configurez les variantes payantes ou gratuites</p>
              </div>
            </div>
            {editingPrestation ? (
              <div className="space-y-6 px-5 py-5">
                {optionsError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {optionsError}
                  </div>
                )}
                {optionsFeedback && (
                  <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                    {optionsFeedback}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500 w-full">Ajout rapide — matériel payant :</span>
                  {[
                    { nom: 'Location raquettes', prix: 8 },
                    { nom: 'Location balles', prix: 5 },
                    { nom: 'Serviette', prix: 3 },
                  ].map((tpl) => (
                    <button
                      key={tpl.nom}
                      type="button"
                      onClick={() => {
                        setOptionForm({
                          ...DEFAULT_OPTION_FORM,
                          nom: tpl.nom,
                          prixSupplement: tpl.prix,
                          description: 'Option à la réservation',
                        });
                      }}
                      className="text-xs px-3 py-1.5 rounded-full border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    >
                      + {tpl.nom} ({tpl.prix} €)
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Nom de la variante*</label>
                    <input
                      type="text"
                      value={optionForm.nom}
                      onChange={(e) => handleOptionFieldChange('nom', e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      placeholder="Balade privative, Option photo..."
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Prix supplémentaire</label>
                    <div className="relative rounded-lg border border-gray-200 shadow-sm focus-within:ring-2 focus-within:ring-indigo-200 focus-within:border-indigo-500">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">€</span>
                      <input
                        type="number"
                        step="0.01"
                        value={optionForm.prixSupplement}
                        onChange={(e) => handleOptionFieldChange('prixSupplement', e.target.value)}
                        className="w-full rounded-lg border-0 pl-8 pr-3 py-2 focus:outline-none"
                        placeholder="0.00 (laisser vide si gratuit)"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Laissez vide pour une option gratuite. Valeur négative = remise.
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Ordre d'affichage</label>
                    <input
                      type="number"
                      value={optionForm.ordre}
                      onChange={(e) => handleOptionFieldChange('ordre', e.target.value)}
                      min="0"
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
                    <textarea
                      value={optionForm.description}
                      onChange={(e) => handleOptionFieldChange('description', e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      placeholder="Détails, prérequis, matériel inclus…"
                    />
                  </div>
                  <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={optionForm.actif}
                      onChange={(e) => handleOptionFieldChange('actif', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Variante active</span>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3">
                  {editingOption && (
                    <button
                      type="button"
                      onClick={handleCancelOptionEdit}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Annuler la modification
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleOptionSubmit}
                    disabled={optionActionId !== null}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {optionActionId !== null && (
                      <span className="mr-2 inline-flex h-4 w-4 items-center justify-center">
                        <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      </span>
                    )}
                    {editingOption ? 'Mettre à jour la variante' : 'Ajouter la variante'}
                  </button>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900">Variantes existantes</h4>
                  {optionsLoading ? (
                    <div className="flex items-center justify-center py-6 text-gray-500">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600 mr-3" />
                      Chargement des variantes…
                    </div>
                  ) : options.length > 0 ? (
                    <div className="space-y-3">
                      {options.map((option) => (
                        <div
                          key={option.id}
                          className="flex flex-col gap-4 rounded-lg border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{option.nom}</span>
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                  option.actif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {option.actif ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <div className="mt-1 text-xs text-gray-600 md:text-sm">
                              {option.prixSupplement !== null && option.prixSupplement !== undefined ? (
                                <span className="font-medium text-indigo-600">
                                  {option.prixSupplement >= 0 ? '+' : '-'}
                                  {Math.abs(option.prixSupplement).toFixed(2)} €
                                </span>
                              ) : (
                                <span className="text-gray-500">Option gratuite</span>
                              )}
                              <span className="ml-3 text-gray-400">Ordre {option.ordre ?? 0}</span>
                            </div>
                            {option.description && (
                              <p className="mt-2 text-xs text-gray-500 md:text-sm">{option.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleToggleOption(option)}
                              disabled={optionActionId === option.id}
                              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 md:text-sm"
                            >
                              {option.actif ? 'Désactiver' : 'Activer'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEditOption(option)}
                              className="rounded-md border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 md:text-sm"
                            >
                              Modifier
                            </button>
                            <button
                              type="button"
                              onClick={() => requestDeleteOption(option)}
                              disabled={optionActionId === option.id}
                              className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 md:text-sm"
                            >
                              Supprimer
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                      Aucune variante définie pour l'instant. Ajoutez une variante ci-dessus.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="px-5 py-5 text-sm text-gray-500">
                Enregistrez la prestation pour ajouter des variantes.
              </div>
            )}
          </div>
          )}

        <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={closePrestationModal}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSavePrestation}
            disabled={
              savingPrestation ||
              !prestationForm.nom ||
              Number(prestationForm.prix) <= 0
            }
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {savingPrestation && (
              <span className="mr-2 inline-flex h-4 w-4 items-center justify-center">
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
              </span>
            )}
            {savingPrestation ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
      </Modal>
      </>
      <ConfirmDialog
        isOpen={!!deleteConfirmation}
        onCancel={() => setDeleteConfirmation(null)}
        onConfirm={confirmDeletePrestation}
        title={deleteConfirmation?.title}
        description={deleteConfirmation?.description}
        tone="danger"
        confirmLabel="Supprimer"
      />
      <ConfirmDialog
        isOpen={!!deleteCreneauConfirmation}
        onCancel={() => {
          setDeleteCreneauConfirmation(null);
          setCreneauActionId(null);
        }}
        onConfirm={confirmDeleteCreneau}
        title={deleteCreneauConfirmation?.title}
        description={deleteCreneauConfirmation?.description}
        tone="danger"
        confirmLabel="Supprimer le créneau"
      />
      <ConfirmDialog
        isOpen={!!deleteOptionConfirmation}
        onCancel={() => setDeleteOptionConfirmation(null)}
        onConfirm={confirmDeleteOption}
        title={deleteOptionConfirmation?.title}
        description={deleteOptionConfirmation?.description}
        tone="danger"
        confirmLabel="Supprimer la variante"
      />

      <PrestationPreviewModal
        isOpen={showPreviewModal}
        onClose={handleClosePreviewModal}
        commercant={commercant}
        prestation={previewPrestation}
      />

      <CommercantHorairesSubModal
        isOpen={showHorairesSubModal}
        onClose={() => setShowHorairesSubModal(false)}
        commercant={commercant}
        onSave={handleSaveHorairesBoutique}
        saving={savingHoraires}
      />
    </div>
  );
});

export default ServicesManager;
